<template>
  <div
    class="flex flex-col"
    data-testid="form-input">
    <div class="items-top mb-2 flex justify-between">
      <label
        :for="id"
        data-testid="input-label"
        class="font-bold"
        :class="[disabled ? 'text-royal-blue-20' : 'text-royal-blue-160']"
      >
        {{ label }}
      </label>
      <span
        v-if="isOptional"
        data-testid="input-optional"
        :class="[disabled ? 'text-royal-blue-20' : 'text-royal-blue-160']"
      >
        optional
      </span>
    </div>
    <template v-if="helpText">
      <p
        :id="helpTextId"
        data-testid="input-help-text"
        class="mb-2"
        :class="[
          disabled
            ? 'cursor-not-allowed text-royal-blue-20'
            : 'cursor-pointer text-royal-blue-160',
        ]"
      >
        {{ helpText }}
      </p>
    </template>
    <div
      class="flex"
      :class="{ 'space-x-2': button }">
      <div
        class="group flex w-full rounded-sm border-2 border-dark-grey"
        :class="{
          'focus-within:border-royal-blue-160 hover:border-royal-blue-100':
            !disabled && !readonly,
          'border-royal-blue-20 focus-within:border-royal-blue-20 hover:border-royal-blue-20':
            disabled || readonly,
          'border-ruby-red-100 focus-within:border-ruby-red-100 hover:border-ruby-red-100':
            errorMessage,
        }"
      >
        <span
          v-if="prefix"
          class="flex items-center border-r-2 border-dark-grey px-4 group-focus-within:border-royal-blue-160 group-hover:border-royal-blue-100"
          :class="{
            'border-royal-blue-20 text-royal-blue-20 hover:border-royal-blue-20 focus:border-royal-blue-20 group-focus-within:border-royal-blue-20 group-hover:border-royal-blue-20':
              disabled || readonly,
            'border-ruby-red-100 hover:border-ruby-red-100 focus:border-ruby-red-100 group-focus-within:border-ruby-red-100 group-hover:border-ruby-red-100':
              errorMessage,
          }"
        >{{ prefix }}</span
        >
        <input
          v-bind="$attrs"
          :id="id"
          ref="input"
          data-testid="input-field"
          :type="type"
          :value="modelValue"
          @input="$emit('update:modelValue', $event.target.value)"
          @change="$emit('custom-change', $event)"
          @focus="$emit('custom-focus', $event)"
          @blur="$emit('custom-blur', $event)"
          :placeholder="placeholder || undefined"
          :disabled="disabled"
          :readonly="readonly"
          :required="!isOptional"
          :aria-controls="ariaControls"
          :aria-describedby="describedById || undefined"
          :aria-invalid="isInvalid"
          :aria-errormessage="errorId"
          class="block w-full flex-1 appearance-none rounded-sm bg-white px-4 py-2.5 placeholder-graphite focus:outline-hidden"
          :class="{
            'text-graphite': !disabled && !readonly,
            'cursor-not-allowed text-royal-blue-20 placeholder-royal-blue-20':
              disabled || readonly,
          }"
        />
      </div>

      <ButtonComponent
        v-if="button"
        :id="buttonId"
        :label="buttonLabel"
        :variant="buttonVariant"
        @customClick="onButtonClick"
      />
    </div>

    <span
      v-if="prefix"
      :id="prefixId"
      class="sr-only">{{
        prefixDescription || prefix
      }}</span>

    <div :aria-live="errorAriaLive">
      <div
        v-if="isInvalid"
        :id="errorId"
        class="items-top mt-4 flex space-x-2 bg-ruby-red-5 rounded p-4">
        <WarningTriangleIcon
          title="Error:"
          class="shrink-0 text-ruby-red-100"
        />
        <p data-testid="input-error-message">
          {{ errorMessage }}
        </p>
      </div>
    </div>
  </div>
</template>

<script>
import ButtonComponent from './ButtonComponent.vue'
import WarningTriangleIcon from './icons/WarningTriangleIcon.vue'

/** Form inputs are input fields for users to enter text or numbers that are no longer than a single line. */
export default {
  name: 'FormInput',
  inheritAttrs: false,
  props: {
    id: {
      type: String,
      required: true,
    },
    /** Communicates the content the user needs to enter in the field. */
    label: {
      type: String,
      default: '',
    },
    /** Communicates to the user that entering content is optional for this field. */
    isOptional: {
      type: Boolean,
      default: false,
    },
    /** The content the user has entered into the field. */
    modelValue: {
      type: [String, Number],
      default: '',
    },
    prefix: {
      type: String,
      default: '',
    },
    prefixDescription: {
      type: String,
      default: '',
    },
    type: {
      type: String,
      default: 'text',
    },
    placeholder: {
      type: String,
      default: '',
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    readonly: {
      type: Boolean,
      default: false,
    },
    /** Accessibility. If input controls another element(s), specify the controlled element(s) ids. */
    ariaControls: {
      type: String,
      default: null,
    },
    errorMessage: {
      type: String,
      default: null,
    },
    errorAriaLive: {
      /** Choose how error messages are announced; interrupting or otherwise. Consideration for scenarios when multiple Form Components may be invalid simulataneously and to not overwhelm. */
      type: String,
      validator: value => ['polite', 'assertive', 'off'].includes(value),
      default: 'polite',
    },
    /** Provides additional feedback about what the user is required to do */
    helpText: {
      type: String,
      default: null,
    },
    button: {
      type: Boolean,
      default: false,
    },
    buttonLabel: {
      type: String,
      default: 'Button',
    },
    buttonVariant: {
      type: String,
      default: 'secondary',
    },
    buttonId: {
      type: String,
      default: null,
    },
  },
  computed: {
    isInvalid() {
      return !!this.errorMessage
    },
    errorId() {
      return this.isInvalid ? `error-${this.id}` : null
    },
    helpTextId() {
      return this.helpText ? `help-${this.id}` : null
    },
    prefixId() {
      return this.prefix ? `prefix-${this.id}` : null
    },
    describedById() {
      // to maximise SR support, errorId used in aria-describedBy & aria-errormessage attributes
      return [this.errorId, this.helpTextId, this.prefixId]
        .filter(id => id)
        .join(' ')
    },
  },
  methods: {
    onButtonClick() {
      this.$emit('button-click')
    },
  },
  components: {
    ButtonComponent,
    WarningTriangleIcon,
  },
  emits: [
    'update:modelValue',
    'custom-change',
    'custom-focus',
    'custom-blur',
    'button-click',
  ], // custom-* events available for potential input validation/error message handling
}
</script>
